import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpenCheck,
  CalendarClock,
  ChevronDown,
  CircleHelp,
  FolderOpen,
  HelpCircle,
  Megaphone,
  Search,
  ShieldCheck,
  Smartphone,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { EmptyState, PageHeader, SectionCard } from "@/components/doxa/AdminSurface";

const CATEGORIES = [
  { id: "all", label: "Tudo", icon: CircleHelp },
  { id: "start", label: "Comece por aqui", icon: Smartphone },
  { id: "academic", label: "Acadêmico", icon: BookOpenCheck },
  { id: "communication", label: "Comunicação", icon: Megaphone },
  { id: "management", label: "Gestão e acesso", icon: UserCog },
  { id: "system", label: "Sistema", icon: ShieldCheck },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((item) => [item.id, item.label]));

const QUICK_LINKS = [
  { to: "/students", label: "Alunos", description: "Cadastro e acompanhamento", icon: Users },
  { to: "/lessons", label: "Aulas", description: "Agenda, presença e QR", icon: CalendarClock },
  { to: "/materials", label: "Materiais", description: "Conteúdos da turma", icon: FolderOpen },
  { to: "/notices", label: "Avisos", description: "Comunicação com alunos", icon: Megaphone },
];

const TOPICS = [
  {
    id: "mobile-navigation",
    category: "start",
    q: "Como navegar pelo Doxa School no celular?",
    a: "Na barra inferior do Admin ficam os atalhos principais: Dashboard, Alunos, Aulas e Tarefas. Toque em “Menu” para abrir as demais áreas liberadas para o seu perfil, acessar a Ajuda e sair da conta.",
    keywords: "celular mobile menu navegação barra inferior",
  },
  {
    id: "install-pwa",
    category: "start",
    q: "Como instalar o Doxa School no celular?",
    a: "No Android/Chrome, use a opção de instalar o app quando ela estiver disponível. No iPhone/Safari, toque em Compartilhar e depois em “Adicionar à Tela de Início”. O Doxa School passa a abrir como um aplicativo, mantendo o mesmo acesso da versão web.",
    keywords: "pwa instalar android iphone safari chrome tela inicial aplicativo",
  },
  {
    id: "missing-module",
    category: "start",
    q: "Por que alguns módulos ou ações não aparecem para mim?",
    a: "O menu e os botões respeitam seu perfil e as permissões configuradas. Master possui acesso global; Admin e Professor podem ter capacidades diferentes. Se uma área necessária não aparecer, confirme com o Master se o seu perfil possui a permissão correspondente.",
    keywords: "permissão acesso módulo menu master admin professor botão escondido",
  },
  {
    id: "qr-difference",
    category: "start",
    q: "Qual é a diferença entre o QR da turma e o QR da aula?",
    a: "O QR da turma serve para inscrição/cadastro naquela turma. O QR da aula serve para presença/check-in. São fluxos diferentes: QR da turma = inscrição; QR da aula = presença.",
    keywords: "qr turma aula inscrição presença checkin diferença",
  },
  {
    id: "new-student",
    category: "academic",
    q: "Como cadastrar um aluno?",
    a: "Acesse Alunos e toque em “Novo aluno”. Preencha os dados solicitados e salve. Quando o fluxo apresentar o link de primeiro acesso, copie-o e envie para a pessoa definir a senha e ativar o acesso.",
    keywords: "aluno cadastrar novo primeiro acesso convite senha",
    to: "/students",
    cta: "Abrir Alunos",
  },
  {
    id: "class-modules",
    category: "academic",
    q: "Como criar uma turma e organizar seus módulos?",
    a: "Acesse Turmas, crie a turma e preencha os dados estruturais. Depois use a área de módulos da própria turma para cadastrar, editar e organizar a ordem do conteúdo sem perder o histórico existente.",
    keywords: "turma módulo criar organizar ordem conteúdo",
    to: "/classes",
    cta: "Abrir Turmas",
  },
  {
    id: "registration-qr",
    category: "academic",
    q: "Como gerar o QR ou link de inscrição de uma turma?",
    a: "Em Turmas, abra o menu de ações da turma ativa e escolha “Inscrição / QR”. O sistema mostra o QR Code e o link público da turma para compartilhar com quem vai se inscrever.",
    keywords: "turma inscrição qr link público compartilhar",
    to: "/classes",
    cta: "Abrir Turmas",
  },
  {
    id: "registrations",
    category: "academic",
    q: "Como aprovar ou recusar inscrições pendentes?",
    a: "Acesse Inscrições e abra a visão “Pendentes”. Você pode aprovar ou recusar uma inscrição individualmente. Para aprovar várias de uma vez, selecione as desejadas e use “Aprovar selecionados”, confirmando a operação antes de continuar.",
    keywords: "inscrição pendente aprovar recusar massa selecionar",
    to: "/registrations",
    cta: "Abrir Inscrições",
  },
  {
    id: "new-lesson",
    category: "academic",
    q: "Como cadastrar uma aula?",
    a: "Acesse Aulas e toque em “Nova aula”. Escolha a turma e o módulo quando aplicável, preencha os dados da aula e salve. Aulas futuras podem permanecer agendadas até o momento de abertura.",
    keywords: "aula cadastrar nova agendar turma módulo",
    to: "/lessons",
    cta: "Abrir Aulas",
  },
  {
    id: "attendance",
    category: "academic",
    q: "Como lançar presença ou justificar uma ausência?",
    a: "Abra o detalhe da aula. Na lista de alunos, use os controles de presença para registrar o estado correspondente. Ao justificar uma ausência, informe o motivo solicitado. Falta justificada continua sendo diferente de presença e segue a regra própria de frequência.",
    keywords: "presença falta ausência justificar manual aluno aula",
    to: "/lessons",
    cta: "Abrir Aulas",
  },
  {
    id: "attendance-qr",
    category: "academic",
    q: "Como funciona o QR Code de presença?",
    a: "No detalhe de uma aula elegível, o QR de presença fica disponível para a operação da aula. O aluno pode usar o fluxo de scanner no Portal do Aluno ou abrir o link válido pela câmera do celular. O backend impede duplicidade de presença para a mesma aula e aluno.",
    keywords: "qr presença scanner câmera aluno checkin duplicidade",
    to: "/lessons",
    cta: "Abrir Aulas",
  },
  {
    id: "tasks",
    category: "academic",
    q: "Como cadastrar uma tarefa?",
    a: "Acesse Tarefas e toque em “Nova tarefa”. Defina os dados e o público da atividade conforme os campos exibidos. Use a data de entrega e, quando necessário, a visibilidade programada para controlar quando a tarefa aparece para os alunos.",
    keywords: "tarefa atividade nova entrega visível programada",
    to: "/tasks",
    cta: "Abrir Tarefas",
  },
  {
    id: "materials",
    category: "academic",
    q: "Como cadastrar um material?",
    a: "Acesse Materiais e toque em “Novo material”. Preencha o título, defina o público-alvo e informe o link do conteúdo conforme o formulário. O material fica disponível ao público definido quando estiver ativo.",
    keywords: "material novo link turma célula aluno público conteúdo",
    to: "/materials",
    cta: "Abrir Materiais",
  },
  {
    id: "evaluations",
    category: "academic",
    q: "Como criar e publicar uma avaliação?",
    a: "Acesse Avaliações e toque em “Nova avaliação”. O fluxo inicial é dividido em etapas para definir dados, público e regras. Depois use o editor para montar as questões e só publique quando a avaliação estiver pronta. As ações disponíveis dependem das permissões e do estado da avaliação.",
    keywords: "avaliação prova nova publicar questões editor tentativa",
    to: "/evaluations",
    cta: "Abrir Avaliações",
  },
  {
    id: "grading",
    category: "academic",
    q: "Onde corrigir respostas dissertativas?",
    a: "Acesse Correções. A fila mostra respostas dissertativas que ainda precisam de correção manual. Abra o item, confira a resposta do aluno, informe a pontuação e o comentário quando necessário e salve a correção.",
    keywords: "correção dissertativa resposta pontuação comentário professor",
    to: "/grading",
    cta: "Abrir Correções",
  },
  {
    id: "notices",
    category: "communication",
    q: "Como publicar um aviso?",
    a: "Acesse Avisos, crie o conteúdo e confira o público antes de publicar. O aviso permanece disponível no sistema para os alunos correspondentes. Recursos de push respeitam as permissões, preferências e confirmações próprias do fluxo de notificações.",
    keywords: "aviso comunicação publicar aluno turma push",
    to: "/notices",
    cta: "Abrir Avisos",
  },
  {
    id: "notifications",
    category: "communication",
    q: "Como acompanhar as notificações push?",
    a: "Acesse Notificações para consultar o status de ativação por turma e o histórico de disparos. A tela também ajuda a identificar quem ainda não ativou, quem bloqueou e quando faz sentido mostrar novamente o convite de notificações.",
    keywords: "notificação push status turma histórico convite bloqueado ativar",
    to: "/notifications",
    cta: "Abrir Notificações",
  },
  {
    id: "cells",
    category: "management",
    q: "Como gerenciar células?",
    a: "Acesse Células para criar, editar, inativar ou reativar registros conforme sua permissão. A inativação preserva o histórico; a gestão da célula não deve ser tratada como exclusão definitiva do registro.",
    keywords: "célula criar editar inativar reativar histórico",
    to: "/cells",
    cta: "Abrir Células",
  },
  {
    id: "users",
    category: "management",
    q: "Como gerenciar conta, acesso ou primeiro acesso de uma pessoa?",
    a: "Acesse Usuários, a Central de Contas e Acessos. Nela você pode acompanhar o estado da conta e, quando permitido, editar dados de acesso, redefinir o primeiro acesso, ativar/inativar ou aplicar a remoção lógica. A situação acadêmica do aluno continua sendo tratada separadamente em Alunos.",
    keywords: "usuário conta acesso convite redefinir senha ativar inativar remover lógico",
    to: "/users",
    cta: "Abrir Usuários",
  },
  {
    id: "reports",
    category: "management",
    q: "Onde consultar indicadores e relatórios?",
    a: "Quando seu perfil possui acesso, a área Relatórios reúne indicadores e análises da operação acadêmica. Use os filtros da própria tela para recortar a leitura por turma, período ou condição disponível, sem alterar os dados de origem.",
    keywords: "relatório indicador analytics turma frequência análise",
    to: "/reports",
    cta: "Abrir Relatórios",
  },
  {
    id: "permissions",
    category: "system",
    q: "Como funcionam os perfis e permissões?",
    a: "A área Permissões é exclusiva do Master. Ela define capacidades dos perfis Admin e Professor; não cria permissão individual por usuário. Master permanece como superusuário fixo e as autorizações reais são aplicadas pelo backend.",
    keywords: "permissão perfil master admin professor rbac acesso",
  },
  {
    id: "audit",
    category: "system",
    q: "Para que serve a Auditoria?",
    a: "Quando seu perfil possui acesso, Auditoria permite consultar eventos operacionais registrados pelo sistema, ajudando a entender quem executou determinadas ações e em qual contexto. É uma consulta de rastreabilidade; não substitui as telas de operação.",
    keywords: "auditoria histórico evento rastreabilidade ação usuário",
    to: "/audit",
    cta: "Abrir Auditoria",
  },
];

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function TopicItem({ topic, open, onToggle, index }) {
  const panelId = `help-panel-${topic.id}`;
  const triggerId = `help-trigger-${topic.id}`;

  return (
    <article className="overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white" data-testid={`help-item-${index}`}>
      <button
        id={triggerId}
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-[#FFFAF6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#F75A1B]/35 sm:px-5"
        data-testid={`help-toggle-${index}`}
      >
        <div className="min-w-0">
          <span className="inline-flex rounded-full bg-[#FFF0E9] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#D94A12]">
            {CATEGORY_LABEL[topic.category]}
          </span>
          <p className="mt-2 break-words text-sm font-semibold text-[#1F1F1F] sm:text-base">{topic.q}</p>
        </div>
        <span className="mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#F8F3EA] text-[#6B655E]">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          className="border-t border-[#EEE7DD] px-4 py-4 sm:px-5"
          data-testid={`help-answer-${index}`}
        >
          <p className="text-sm leading-6 text-[#4A4640]">{topic.a}</p>
          {topic.to && (
            <Link
              to={topic.to}
              className="mt-4 inline-flex items-center gap-1 rounded-lg text-xs font-semibold text-[#F75A1B] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30"
            >
              {topic.cta || "Abrir área"}<ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}
    </article>
  );
}

export default function Help() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [openId, setOpenId] = useState("mobile-navigation");

  const filteredTopics = useMemo(() => {
    const needle = normalizeText(query);
    return TOPICS.filter((topic) => {
      if (category !== "all" && topic.category !== category) return false;
      if (!needle) return true;
      const haystack = normalizeText(`${topic.q} ${topic.a} ${topic.keywords} ${CATEGORY_LABEL[topic.category]}`);
      return haystack.includes(needle);
    });
  }, [query, category]);

  const selectCategory = (next) => {
    setCategory(next);
    setOpenId(null);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 md:p-8" data-testid="help-page">
      <PageHeader
        eyebrow="Ajuda"
        title="Central de Ajuda"
        description="Encontre orientações rápidas sobre os fluxos do Doxa School sem precisar sair da operação."
        meta="O que aparece no sistema depende do seu perfil e das permissões configuradas."
      />

      <SectionCard
        title="Atalhos mais usados"
        description="Abra rapidamente as áreas consultadas com mais frequência no dia a dia."
        icon={HelpCircle}
        compact
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {QUICK_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group flex items-center gap-3 rounded-2xl border border-[#E0D9CD] bg-[#FFFCF9] p-4 transition hover:-translate-y-0.5 hover:border-[#F75A1B]/35 hover:shadow-[0_8px_24px_rgba(68,53,35,0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]">
                <item.icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[#1F1F1F]">{item.label}</span>
                <span className="mt-0.5 block text-xs text-[#6B655E]">{item.description}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-[#B0A79B] transition group-hover:translate-x-0.5 group-hover:text-[#F75A1B]" />
            </Link>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Dúvidas e orientações"
        description="Pesquise por uma tarefa ou filtre pelos temas da operação."
        icon={BookOpenCheck}
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A8378]" />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpenId(null);
            }}
            placeholder="Buscar por aluno, QR, presença, material, permissão..."
            aria-label="Buscar na Central de Ajuda"
            className="w-full rounded-xl border border-[#D8CFC2] bg-white py-3 pl-10 pr-10 text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#A49B90] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
            data-testid="help-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Limpar busca"
              className="absolute right-2.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-[#8A8378] hover:bg-[#F6EFE4] hover:text-[#1F1F1F]"
              data-testid="help-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1 scrollbar-hide" aria-label="Categorias da Central de Ajuda">
          {CATEGORIES.map((item) => {
            const active = category === item.id;
            const count = item.id === "all" ? TOPICS.length : TOPICS.filter((topic) => topic.category === item.id).length;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectCategory(item.id)}
                aria-pressed={active}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 ${
                  active
                    ? "border-[#F75A1B] bg-[#FFF0E9] text-[#D94A12]"
                    : "border-[#E0D9CD] bg-white text-[#6B655E] hover:border-[#F75A1B]/35 hover:text-[#D94A12]"
                }`}
                data-testid={`help-category-${item.id}`}
              >
                <item.icon className="h-3.5 w-3.5" />
                {item.label}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${active ? "bg-white/70" : "bg-[#F6EFE4]"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#EEE7DD] pt-4">
          <p className="text-xs font-medium text-[#8A8378]" data-testid="help-result-count">
            {filteredTopics.length} {filteredTopics.length === 1 ? "orientação encontrada" : "orientações encontradas"}
          </p>
          {(query || category !== "all") && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setCategory("all");
                setOpenId(null);
              }}
              className="text-xs font-semibold text-[#F75A1B] hover:text-[#D94A12]"
              data-testid="help-reset-filters"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {filteredTopics.length ? (
          <div className="mt-4 space-y-3">
            {filteredTopics.map((topic, index) => (
              <TopicItem
                key={topic.id}
                topic={topic}
                index={index}
                open={openId === topic.id}
                onToggle={() => setOpenId(openId === topic.id ? null : topic.id)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Search}
            title="Nada encontrado"
            description="Tente outro termo ou limpe os filtros para ver todas as orientações disponíveis."
          />
        )}
      </SectionCard>
    </div>
  );
}
